/**
 * Thin fetch wrapper for the admin SPA (admin-arch §3.0.5, §7).
 *
 * Responsibilities:
 * - Inject the in-memory access token via `Authorization: Bearer …`.
 * - Send `credentials: 'include'` so the httpOnly refresh cookie travels with every call.
 * - On a single `401`, transparently call `/auth/refresh` once (single-flight mutex)
 *   and retry the original request. If refresh fails, emit a `session-expired`
 *   `CustomEvent` so the `<SessionExpiredModal />` can prompt the user.
 *
 * The access token is *never* persisted to browser storage. It lives exclusively
 * in React state via `auth-provider.tsx`; this module reaches into it through
 * the `bindAuth()` callbacks below.
 */

let refreshPromise: Promise<string | null> | null = null;
let getCurrentToken: () => string | null = () => null;
let setNewToken: (token: string | null) => void = () => {};

/**
 * Wire the api-client to the live auth context. Called once from `AuthProvider`
 * after it mounts so the closure sees the current token getter/setter.
 */
export function bindAuth(
  getter: () => string | null,
  setter: (token: string | null) => void,
): void {
  getCurrentToken = getter;
  setNewToken = setter;
}

export const SESSION_EXPIRED_EVENT = 'session-expired';

export interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    retry_after?: number;
  };
  requires_2fa?: boolean;
  requires_2fa_setup?: boolean;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly envelope: ApiErrorEnvelope;

  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(envelope?.error?.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = envelope;
  }

  get code(): string | undefined {
    return this.envelope?.error?.code;
  }

  get retryAfter(): number | undefined {
    return this.envelope?.error?.retry_after;
  }
}

async function performRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
      const resp = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) {
        setNewToken(null);
        return null;
      }
      const data = (await resp.json()) as { access_token?: string };
      const token = data?.access_token ?? null;
      setNewToken(token);
      return token;
    } catch {
      setNewToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function readEnvelope(resp: Response): Promise<ApiErrorEnvelope> {
  try {
    return (await resp.json()) as ApiErrorEnvelope;
  } catch {
    return {};
  }
}

export interface ApiClientOptions extends RequestInit {
  /** When true, suppress the global `session-expired` event on 401. Useful for
   * the login mutation itself (a 401 there means "bad credentials", not
   * "session ended"). */
  skipSessionExpiredEvent?: boolean;
}

export async function apiClient<T>(
  path: string,
  init: ApiClientOptions = {},
  _retried = false,
): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  const { skipSessionExpiredEvent, ...fetchInit } = init;
  const token = getCurrentToken();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((fetchInit.headers as Record<string, string> | undefined) ?? {}),
  };
  if (fetchInit.body !== undefined && !(fetchInit.body instanceof FormData)) {
    headers['Content-Type'] ??= 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    ...fetchInit,
    headers,
  });

  if (resp.status === 401 && !_retried) {
    const newToken = await performRefresh();
    if (newToken) {
      return apiClient<T>(path, init, true);
    }
    if (!skipSessionExpiredEvent) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    throw new ApiError(401, await readEnvelope(resp));
  }

  if (!resp.ok) {
    throw new ApiError(resp.status, await readEnvelope(resp));
  }

  if (resp.status === 204) {
    return undefined as T;
  }
  return (await resp.json()) as T;
}
